function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { InvoiceDisplayStatus } from 'anchor-scout-common/types';
import { FullPageModalAdditionalActions, useOverlayContext } from 'seagull';
import { DocumentCheckmark24, AttachFile, MoneyChange, MoneyReceive, RedoLiner24, SyncLiner24, Void } from 'seagull/icons';
import { getRefundActionConfig, allowResendAction, allowVoidAction, allowUnvoidAction, allowUnmarkPaidExternallyAction } from "../../business-logic/invoiceLogic";
import { routes } from "../../constants";
import { useStore, useStoreActions } from "../../contexts";
import { useDownloadInvoicePdf } from "../../hooks/invoice/useDownloadInvoicePdf";
import { useCopyDirectLink } from "../../hooks/useCopyDirectLink";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { usePaidExternally } from "../../hooks/usePaidExternally";
import { useResendInvoice } from "../../hooks/useResendInvoice";
import { Intent } from "../../infra/access-control";
import { getAccountingDetails } from "../../integrations/Accounting/accounting.utils";
import { invoiceModel, relationshipModel } from "../../models";
import { selectors } from "../../store/state-selectors";
import { Accounting } from "../../types/api.model";
import { makeResumePaymentInvoiceAction, terminatedTooltip } from "./FullPageInvoice.utils";
import { useCanResyncQbo } from "./hooks/useCanResyncQbo";
import { useCanResyncXero } from "./hooks/useCanResyncXero";
import { useResyncQbo } from "./hooks/useResyncQbo";
import { useResyncXero } from "./hooks/useResyncXero";
import { jsx as _jsx } from "react/jsx-runtime";
export const FullPageInvoiceAdditionalActionsTestIds = pathgen('FullPageInvoiceAdditionalActionsTestIds');
export default function FullPageInvoiceAdditionalActions(props) {
  const {
    invoice,
    relationship,
    isVendor,
    reloadInvoice,
    onStopPayment,
    onResumePayment,
    onVoidInvoice,
    onUnvoidInvoice,
    onUnmarkPaidExternally,
    onRequestRefund,
    onAttachFilesClick,
    isUploadingAttachments
  } = props;
  const {
    UNVOID_INVOICE = false,
    UNMARK_PAID_EXTERNALLY = false,
    HEADER_LAYOUT_UPDATES
  } = useFeatureFlags();
  const {
    shouldShowMarkAsPaid,
    markAsPaid
  } = usePaidExternally();
  const {
    isResendingInvoice,
    resendInvoice
  } = useResendInvoice();
  const {
    isFetchingLink,
    copyDirectLink
  } = useCopyDirectLink();
  const {
    logBI
  } = useStoreActions();
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const isClientMember = !isVendor && isMemberUser;
  const {
    isDownloadingInvoicePdf,
    downloadInvoicePdf
  } = useDownloadInvoicePdf();
  const history = useHistory();
  const isTerminated = relationshipModel.isTerminated(relationship);
  const {
    XERO_INTEGRATION,
    RESYNC_INVOICES,
    ATTACH_FILES
  } = useFeatureFlags();
  const {
    showConfirmAction
  } = useOverlayContext();
  const refundActionConfig = getRefundActionConfig({
    invoice,
    isVendor
  });
  const isInvoiceDisputed = invoice.displayStatus === InvoiceDisplayStatus.PaymentDisputed;
  const showQboIntegration = invoice.integration.isSynced() && !invoice.integration.isRefundRecorded() && !isInvoiceDisputed;
  const showXeroIntegration = XERO_INTEGRATION && invoice.xeroIntegration.isPaymentRecorded() && !isInvoiceDisputed;
  const showResyncQboIntegration = RESYNC_INVOICES && showQboIntegration && isVendor;
  const showResyncXeroIntegration = RESYNC_INVOICES && showXeroIntegration && isVendor;
  const showAttachFilesIntegration = ATTACH_FILES && isVendor;
  const {
    canResyncQbo,
    refreshQbo
  } = useCanResyncQbo({
    enabled: !!showResyncQboIntegration,
    invoiceId: invoice.id
  });
  const {
    canResyncXero,
    refreshXero
  } = useCanResyncXero({
    enabled: !!showResyncXeroIntegration,
    invoiceId: invoice.id
  });
  const {
    resyncQbo
  } = useResyncQbo({
    onSettled: () => refreshQbo(),
    isPaymentRecorded: invoice.integration.isPaymentRecorded()
  });
  const {
    resyncXero
  } = useResyncXero({
    onSettled: () => refreshXero()
  });
  const disabledResyncTooltip = 'Paid invoices already included in a synced payout can’t be resynced';
  const isResyncQboIntegrationDisabled = isClientMember || !canResyncQbo;
  const isResyncXeroIntegrationDisabled = isClientMember || !canResyncXero;
  const handleResync = _ref => {
    let {
      isPaymentRecorded,
      isInvoiceRecorded,
      agreementName,
      onConfirm
    } = _ref;
    let content;
    if (isPaymentRecorded && isInvoiceRecorded) {
      content = "A new invoice and payment will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous invoice and payment are first deleted from ").concat(agreementName, ". Please ensure they are removed before continuing.");
    } else if (isPaymentRecorded) {
      content = "A new payment will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous payment is first deleted from ").concat(agreementName, ". Please ensure it is removed before continuing.");
    } else if (isInvoiceRecorded) {
      content = "A new invoice will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous invoice is first deleted from ").concat(agreementName, ". Please ensure it is removed before continuing.");
    } else {
      return;
    }
    showConfirmAction({
      title: "Re-sync to ".concat(agreementName),
      content,
      primaryButtonLabel: 'Re-sync',
      secondaryButtonLabel: 'Cancel',
      width: '610px',
      onConfirm
    });
  };
  const handleResyncQbo = () => {
    handleResync({
      isPaymentRecorded: invoice.integration.isPaymentRecorded(),
      isInvoiceRecorded: invoice.integration.isPaymentRecorded() || invoice.integration.isInvoiceRecorded(),
      agreementName: getAccountingDetails(Accounting.QBO).fullName,
      onConfirm: () => resyncQbo(invoice.id)
    });
  };
  const handleResyncXero = () => {
    const integration = invoice.xeroIntegration.getIntegration();
    handleResync({
      isPaymentRecorded: integration.isPaymentRecorded(),
      isInvoiceRecorded: false,
      agreementName: 'Xero',
      onConfirm: () => resyncXero(invoice.id)
    });
  };
  const inlineActions = compact([{
    text: 'Download PDF',
    testid: 'fpi-download-pdf',
    intent: Intent.InvoiceDownload,
    disabled: isDownloadingInvoicePdf || isClientMember || !invoice.pdfExists,
    tooltipTitle: invoice.pdfExists ? undefined : 'Generating PDF',
    onClick: () => downloadInvoicePdf(invoice.id)
  }, allowResendAction(invoiceModel.statusType(invoice), isVendor) && {
    text: 'Resend invoice',
    testid: 'fpi-resend-invoice',
    intent: Intent.InvoiceResend,
    disabled: isResendingInvoice || isClientMember,
    onClick: () => resendInvoice(invoice).then(() => reloadInvoice())
  }, {
    text: 'Copy link',
    testid: 'fpi-copy-link',
    intent: Intent.InvoiceCopyLink,
    disabled: isFetchingLink || isClientMember,
    onClick: () => {
      logBI(14001);
      copyDirectLink(relationship.id, {
        type: 'invoice',
        id: invoice.id
      });
    }
  }]);
  const dropdownActions = compact([{
    text: 'View agreement',
    testid: 'fpi-view-agreement',
    disabled: isClientMember,
    iconStart: /*#__PURE__*/_jsx(DocumentCheckmark24, {}),
    onClick: () => history.push(routes.agreement(relationshipModel.id(relationship)))
  }, shouldShowMarkAsPaid(invoiceModel.statusType(invoice), isVendor) && {
    text: 'Mark as paid externally',
    testid: 'fpi-mark-paid-externally',
    intent: Intent.InvoiceMarkPaidExternally,
    disabled: isClientMember,
    iconStart: /*#__PURE__*/_jsx(MoneyReceive, {}),
    onClick: () => markAsPaid(invoice, () => reloadInvoice())
  }, refundActionConfig.showAction && {
    text: refundActionConfig.text,
    testid: 'fpi-request-refund',
    intent: refundActionConfig.intent,
    iconStart: /*#__PURE__*/_jsx(MoneyChange, {}),
    onClick: onRequestRefund,
    tooltipTitle: refundActionConfig.tooltipTitle,
    disabled: refundActionConfig.disabled
  }, showResyncQboIntegration && {
    text: "Re-sync to ".concat(getAccountingDetails(Accounting.QBO).fullName),
    testid: FullPageInvoiceAdditionalActionsTestIds.MoreActions.ResyncQboInvoices(),
    disabled: isResyncQboIntegrationDisabled,
    tooltipTitle: !canResyncQbo ? disabledResyncTooltip : undefined,
    iconStart: /*#__PURE__*/_jsx(SyncLiner24, {}),
    onClick: handleResyncQbo
  }, showResyncXeroIntegration && {
    text: 'Re-sync payment to Xero',
    testid: FullPageInvoiceAdditionalActionsTestIds.MoreActions.ResyncXeroInvoices(),
    disabled: isResyncXeroIntegrationDisabled,
    tooltipTitle: !canResyncXero ? disabledResyncTooltip : undefined,
    iconStart: /*#__PURE__*/_jsx(SyncLiner24, {}),
    onClick: handleResyncXero
  }, showAttachFilesIntegration && {
    text: 'Attach files',
    testid: FullPageInvoiceAdditionalActionsTestIds.MoreActions.AttachFiles(),
    iconStart: /*#__PURE__*/_jsx(AttachFile, {}),
    onClick: onAttachFilesClick,
    disabled: isUploadingAttachments
  }, allowVoidAction(invoiceModel.statusType(invoice), isVendor) && {
    text: 'Void invoice',
    testid: 'fpi-void-invoice',
    intent: Intent.InvoiceVoid,
    skin: 'error',
    iconStart: /*#__PURE__*/_jsx(Void, {}),
    onClick: onVoidInvoice
  }, allowUnvoidAction(invoice, isVendor, UNVOID_INVOICE) && {
    text: 'Unvoid invoice',
    testid: 'fpi-unvoid-invoice',
    intent: Intent.InvoiceUnvoid,
    iconStart: /*#__PURE__*/_jsx(RedoLiner24, {}),
    onClick: onUnvoidInvoice
  }, allowUnmarkPaidExternallyAction(invoice, isVendor, UNMARK_PAID_EXTERNALLY) && {
    text: 'Unmark as paid externally',
    testid: 'fpi-unmark-paid-externally',
    intent: Intent.InvoiceUnmarkPaidExternally,
    iconStart: /*#__PURE__*/_jsx(RedoLiner24, {}),
    onClick: onUnmarkPaidExternally
  }, !isVendor && invoiceModel.isPending(invoice) && {
    text: 'Stop Payment',
    intent: Intent.InvoiceStopPayment,
    disabled: isTerminated,
    tooltipTitle: isTerminated ? terminatedTooltip : undefined,
    testid: 'fpi-stop-payment',
    skin: 'error',
    iconStart: /*#__PURE__*/_jsx(Void, {}),
    onClick: onStopPayment
  }, !isVendor && invoiceModel.isPaymentStopped(invoice) && _objectSpread(_objectSpread({}, makeResumePaymentInvoiceAction(isTerminated)), {}, {
    testid: 'fpi-resume-payment',
    iconStart: /*#__PURE__*/_jsx(MoneyReceive, {}),
    onClick: onResumePayment
  })]);
  return /*#__PURE__*/_jsx(FullPageModalAdditionalActions, {
    newUI: HEADER_LAYOUT_UPDATES,
    inlineActions: inlineActions,
    dropdownActions: dropdownActions,
    "data-testid": FullPageInvoiceAdditionalActionsTestIds.FullPageModalAdditionalActions()
  });
}